import config from '@/config';
import { LogicContext } from '@/logic/main/types';
import AlgoliaFactory from '@/model/algolia';
import { isLocalStorageAvailable } from '@/utils';

const CACHE_KEY = 'numberOfAllSearchGames';
const CACHE_EXPIRY_MS = 7 * 24 * 60 * 60 * 1000; // 7 days

export const fetchNumberOfAllSearchGames = async (context: LogicContext) => {
  const locale = `${config.viewer_country}-${config.viewer_region}`;
  const cachedNumberOfGames = getGamesNumberFromStorage(locale);
  if (cachedNumberOfGames !== null) {
    return {
      numberOfAllSearchGames: cachedNumberOfGames,
    };
  }

  const Algolia = AlgoliaFactory({
    ...config.algolia.config,
  });

  const filters = [
    locale,
    `${config.viewer_country}-EMPTY`,
    'EMPTY',
  ];

  const { numberOfGames } = await Algolia.searchNumberOfAllGames(filters);
  saveToStorage(numberOfGames, locale);

  return {
    numberOfAllSearchGames: numberOfGames,
  };
};

/**
 * Get number of all search games from localStorage for user locale if available
 * and not expired.
 *
 * @param locale User locale string based, e.g., 'GB-ENG'. Use this format to
 *   match Algolia filters.
 * @returns Number of all search games or null if not available/expired
 */
function getGamesNumberFromStorage(locale: string): number | null {
  if (!isLocalStorageAvailable()) return null;

  try {
    const storageData = localStorage.getItem(CACHE_KEY);
    if (!storageData) return null;

    const data = JSON.parse(storageData);
    const localeData = data?.[locale];
    if (!localeData) return null;

    const { value, timestamp } = data[locale];
    if (Date.now() - timestamp >= CACHE_EXPIRY_MS) {
      removeLocaleStorageData(locale);
      return null;
    }

    return value;
  } catch (e) {
    console.error(e);
    return null;
  }
}

/**
 * Save number of all search games per locale to localStorage with timestamp.
 *
 * @param value Number of all search games
 * @param locale User locale string, e.g., 'GB-ENG'. Use this format to
 *   match Algolia filters.
 */
function saveToStorage(value: number, locale: string) {
  if (!isLocalStorageAvailable()) return;

  const storageDataString = localStorage.getItem(CACHE_KEY) ?? '{}';
  const data = JSON.parse(storageDataString);
  data[locale] = { value, timestamp: Date.now() };
  localStorage.setItem(
    CACHE_KEY,
    JSON.stringify(data)
  );
}

/**
 * Remove locale-specific data from localStorage.
 *
 * @param locale User locale string, e.g., 'GB-ENG'. Use this format to
 *   match Algolia filters.
 */
function removeLocaleStorageData(locale: string) {
  if (!isLocalStorageAvailable()) return;

  const storageDataString = localStorage.getItem(CACHE_KEY);
  if (!storageDataString) return;

  const data = JSON.parse(storageDataString);
  delete data[locale];
  localStorage.setItem(CACHE_KEY, JSON.stringify(data));
}
